import { defineStore } from "pinia";
import CandidateApi from "../api/CandidateApi";
import {
  isValidString,
  isValidEmail,
  isValidPhone,
  isValidPassword,
} from "../helpers/ValidationRules";
import { useAccountStore } from "./AccountStore";
import {
  checkIfUsernameTaken,
  checkIfEmailExists,
  sendSMSVerificationCode,
  validateField,
  registerUser,
} from "../helpers/CommonAccountFunctions.js";
import {serializeFormData} from "../helpers/CaseTransformer.js";

const accountStore = useAccountStore();

export const useCandidateAccountCreateStore = defineStore(
  "candidateAccountCreate",
  {
    state: () => ({
      username: "",
      firstName: "",
      lastName: "",
      emailAddress: "",
      tosChecked: false,
      enableDefaultSmsNotifications: true,
      password: "",
      passwordConfirmation: "",
      phone: null,
      phoneVerificationCode: "",
      phoneValidated: false,
      industries: [],
      desiredHourlyPay: 15,
      compensationFlexibility: 0.1,
      location: "",
      lat: null,
      lng: null,
      addressLine1: "",
      addressLine2: "",
      city: "",
      state: "",
      postalCode: "",
      country: "",
      prefersHourlyOverSalary: true,
      isGiveaway: false, // Giveaway specific
      useReferralCode: "", // Giveaway specific
      associatedUserID: null,
      referralCode: "",
      errors: {},
    }),
    actions: {
      validateStep1(notifyUserOnError = false) {
        let firstNameValid = validateField(
          isValidString(this.firstName, true, 50),
          notifyUserOnError,
          "First name is required and must be less than 50 characters",
          (error) => (this.errors.firstName = error),
          () => this.clearError("firstName")
        );

        let lastNameValid = validateField(
          isValidString(this.lastName, true, 50),
          notifyUserOnError,
          "Last name is required and must be less than 50 characters",
          (error) => (this.errors.lastName = error),
          () => this.clearError("lastName")
        );

        let emailValid = validateField(
          isValidEmail(this.emailAddress, true),
          notifyUserOnError,
          "Please enter a valid email",
          (error) => (this.errors.emailAddress = error),
          () => this.clearError("emailAddress")
        );

        if (!this.tosChecked) {
          if (notifyUserOnError) {
            toast.warning(
              "You must agree to the Terms of Service and Privacy Policy"
            );
            this.errors.tosChecked =
              "You must agree to the Terms of Service and Privacy Policy";
          }
          return false;
        } else {
          this.clearError("tosChecked");
        }

        return firstNameValid && lastNameValid && emailValid;
      },
      validateStep2(notifyUserOnError = false) {
        let phoneValid = validateField(
          isValidPhone(this.phone, true),
          notifyUserOnError,
          "Please enter a valid phone number",
          (error) => (this.errors.phone = error),
          () => this.clearError("phone")
        );

        let passwordValid = validateField(
          isValidPassword(this.password, true),
          notifyUserOnError,
          "Please enter a valid password",
          (error) => (this.errors.password = error),
          () => this.clearError("password")
        );

        let passwordsMatch = validateField(
          this.password === this.passwordConfirmation,
          notifyUserOnError,
          "Passwords do not match",
          (error) => (this.errors.passwordConfirmation = error),
          () => this.clearError("passwordConfirmation")
        );

        let isValidLocation = validateField(
          this.location.length > 0,
          notifyUserOnError,
          "Please enter a valid location",
          (error) => (this.errors.location = error),
          () => this.clearError("location")
        );

        let isValidLat = validateField(
          this.lat !== null,
          notifyUserOnError,
          "The location entered didn't match any latitude/longitude coordinates. Please enter a valid location.",
          (error) => (this.errors.location = error),
          () => this.clearError("location")
        );

        return phoneValid && passwordValid && passwordsMatch && isValidLocation && isValidLat;
      },
      validateStep3() {
        return this.$state.phoneVerificationCode.length === 6;
      },
      validateStep4() {
        return true;
      },
      validateStep5() {
        return true;
      },
      isValidCode() {
        return true;
      },
      clearError(fieldName) {
        if (!(fieldName in this.errors)) return;
        this.errors[fieldName] = "";
      },
      async checkIfEmailExists() {
        return await checkIfEmailExists(this.emailAddress);
      },
      async checkIfUsernameTaken() {
        return await checkIfUsernameTaken(this.username);
      },
      async sendVerificationSms(callback) {
        await sendSMSVerificationCode(this.phone, callback);
      },
      getUser() {
        return {
          username: this.username,
          first_name: this.firstName,
          last_name: this.lastName,
          phone: this.phone,
          email: this.emailAddress,
          address_line1: this.addressLine1,
          address_line2: this.addressLine2,
          city: this.city,
          state: this.state,
          postal_code: this.postalCode,
          country: this.country,
          lat: this.lat,
          lng: this.lng,
          password: this.password,
          password_confirmation: this.$state.passwordConfirmation,
          sms_verification_code: this.$state.phoneVerificationCode,
          enable_default_sms_notifications: this.enableDefaultSmsNotifications,
          referral_code: this.referralCode
        };
      },
      async register(callback = undefined) {
        await registerUser(
          this.getUser(),
          false,
          null,
          callback,
          (id) => {
            accountStore.fetchUserAccount();
            this.associatedUserID = id;
          },
          this.firstName
        );
      },
      updateCandidate(shouldIncludeIndustries = true) {
        window.progressBar.start();

        let formData = serializeFormData({
          desired_hourly_pay: this.desiredHourlyPay,
          prefers_hourly_over_salary: this.prefersHourlyOverSalary,
          compensation_flexibility: this.compensationFlexibility,
          ...shouldIncludeIndustries && { industries: this.industries },
        });

        new CandidateApi()
          .updateWithFiles(accountStore.user.candidateId, formData)
          .then(() => window.progressBar.complete());
      },
    },
  }
);
